"use client";

import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const reviews = [{
  id: 1,
  author: "Jess Donlevy",
  location: "Surrey • 2\xA0years ago",
  time: "3 years ago",
  rating: 5,
  text: "\"James and his team were superb. No fuss, just provided me with an excellent new air conditioning unit for my bedroom. Very professional but pleasant as well\"",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/generic_avatar_female_1.png"
}, {
  id: 2,
  author: "Josh Taylor",
  location: "Sussex • 1\xA0years ago",
  time: "4 years ago",
  rating: 5,
  text: "Eurotiles and Stone ltd have been using James and his team for a number of years now and we would not hesitate to recommend them to anyone. Great team & great workmanship all round.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/generic_avatar_male_1.png"
}, {
  id: 3,
  author: "Sarah Jenkins",
  location: "Horsham",
  time: "1 year ago",
  rating: 5,
  text: "Fantastic service from start to finish. The installation in our Horsham office was quick and tidy. Highly recommended for any commercial AC needs.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/generic_avatar_female_2.png"
}, {
  id: 4,
  author: "Mark Davies",
  location: "West Sussex",
  time: "2 years ago",
  rating: 5,
  text: "Reliable, efficient, and very knowledgeable. They maintained our climate control systems perfectly throughout the summer heatwave.",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/generic_avatar_male_2.png"
}];
export default function CustomerTestimonialsCarousel() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="customertestimonialscarousel" className="bg-background py-16 md:py-24 overflow-hidden" ref={ref}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="mb-12 text-center lg:text-left">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground">
            What Our Customers Say
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={isVisible ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: "easeOut"
        }} className="lg:col-span-4 flex flex-col items-center lg:items-start space-y-6">
            <div className="flex items-center gap-4">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/generic_business_avatar.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/generic_business_avatar.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717132/img/generic_business_avatar.png-wvc-sizes" alt="Coastline Air Conditioning Services" className="w-16 h-16 rounded-lg object-cover shadow-sm max-w-full" loading="lazy" />
              <div>
                <h3 className="font-bold text-xl font-default text-foreground">
                  Coastline Air Conditioning Services
                </h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-bold text-orange-500 text-lg">5.0</span>
                  <div className="flex">
                    {[...Array(5)].map((_, i) => <Star key={i} data-index={i} className="w-4 h-4 fill-orange-500 text-orange-500" />)}
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Based on 5 reviews</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  powered by <span className="font-semibold text-foreground">Google</span>
                </p>
              </div>
            </div>

            <Button asChild className="rounded-lg px-6 bg-[#1a73e8] hover:bg-[#1557b0] text-white shadow-md">
              <Link href="https://g.page/r/CUkeZ7ejrRGqEAE/review" newTab>
                review us on Google
              </Link>
            </Button>
          </motion.div>

          {}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={isVisible ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.4,
          ease: "easeOut"
        }} className="lg:col-span-8 relative">
            <Carousel opts={{
            align: "start",
            loop: true
          }} className="w-full">
              <CarouselContent className="-ml-4">
                {reviews.map((review, index) => <CarouselItem key={review.id} data-index={index} className="pl-4 md:basis-1/2 lg:basis-full xl:basis-1/2">
                    <Card className="h-full bg-card shadow-md border-border/50 flex flex-col">
                      <CardHeader className="pb-4 flex flex-row items-start gap-4 space-y-0">
                        <img src={review.avatar} data-wvc-srcset={`${review.avatar}-wvc-srcset`} data-wvc-sizes={`${review.avatar}-wvc-sizes`} alt={review.author} className="w-12 h-12 rounded-lg object-cover shadow-sm max-w-full" loading="lazy" />
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground font-default leading-tight">
                            {review.author}
                          </h4>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {review.location} • {review.time}
                          </p>
                        </div>
                        {}
                        <div className="w-6 h-6 rounded-lg bg-muted flex items-center justify-center shrink-0">
                          <span className="text-xs font-bold text-foreground">G</span>
                        </div>
                      </CardHeader>
                      <CardContent className="flex-1 flex flex-col">
                        <div className="flex mb-3">
                          {[...Array(review.rating)].map((_, i) => <Star key={i} data-index={i} className="w-4 h-4 fill-orange-500 text-orange-500" />)}
                        </div>
                        <p className="text-foreground/90 font-serif text-lg leading-relaxed flex-1">
                          "{review.text}"
                        </p>
                      </CardContent>
                    </Card>
                  </CarouselItem>)}
              </CarouselContent>
              <div className="hidden sm:block">
                <CarouselPrevious className="-left-4 lg:-left-6 bg-background shadow-md hover:bg-accent" />
                <CarouselNext className="-right-4 lg:-right-6 bg-background shadow-md hover:bg-accent" />
              </div>
            </Carousel>
          </motion.div>
        </div>
      </div>
    </section>;
}